import { Editor, Element as SlateElement, Transforms, Node, Point, Range, Path } from 'slate';
import { v4 as uuidv4 } from 'uuid';
export const LIST_TYPES = ['numbered-list', 'bulleted-list'];

export const withLists =
  (options = {}) =>
  (editor) => {
    const { insertBreak, normalizeNode, deleteBackward } = editor;

    editor.insertBreak = () => {
      const { selection } = editor;
      if (!selection || !Range.isCollapsed(selection)) {
        return insertBreak();
      }

      const [itemEntry] = Editor.nodes(editor, {
        match: (n) => SlateElement.isElement(n) && n.type === 'list-item',
      });
      if (!itemEntry) {
        return insertBreak();
      }
      const [liNode, liPath] = itemEntry;
      const text = Node.string(liNode);
      const { anchor } = selection;
      const offset = anchor.offset;
      if (text.trim() === '') {
        return Editor.withoutNormalizing(editor, () => {
          Transforms.setNodes(editor, { type: 'paragraph' }, { at: liPath });
          Transforms.unwrapNodes(editor, {
            match: (n) => SlateElement.isElement(n) && LIST_TYPES.includes(n.type),
            split: true,
          });
          const parentPath = Path.parent(liPath);
          const parentNode = Node.get(editor, parentPath);

          const insertIndex = liPath[liPath.length - 1] + 1;
          const insertPath = [...parentPath, insertIndex];
          if (insertIndex <= parentNode.children.length) {
            Transforms.insertNodes(editor, { type: 'paragraph', children: [{ text: '' }] }, { at: insertPath });

            Transforms.select(editor, Editor.start(editor, insertPath));
          }
        });
      }

      if (offset > 0 && offset < text.length) {
        return Editor.withoutNormalizing(editor, () => {
          Transforms.splitNodes(editor, {
            at: anchor,
            match: (n) => SlateElement.isElement(n) && n.type === 'list-item',
            always: true,
          });
          const newPath = Path.next(liPath);
          Transforms.setNodes(editor, { type: 'list-item' }, { at: newPath });
          Transforms.select(editor, Editor.start(editor, newPath));
        });
      }
      if (offset === text.length) {
        return Editor.withoutNormalizing(editor, () => {
          const insertPath = Path.next(liPath);
          Transforms.insertNodes(editor, { type: 'list-item', uuid: uuidv4(), children: [{ text: '' }] }, { at: insertPath });
          Transforms.select(editor, Editor.start(editor, insertPath));
        });
      }
      insertBreak();
    };

    editor.normalizeNode = (entry) => {
      const [node, path] = entry;
      if (SlateElement.isElement(node) && LIST_TYPES.includes(node.type)) {
        if (!node.uuid) {
          Transforms.setNodes(editor, { uuid: uuidv4() }, { at: path });
        }
        for (const [childNode, childPath] of Node.children(editor, path)) {
          if (!SlateElement.isElement(childNode) || childNode.type !== 'list-item') {
            Transforms.wrapNodes(editor, { type: 'list-item', uuid: uuidv4(), children: [] }, { at: childPath });
            return;
          }
        }
      }

      if (SlateElement.isElement(node) && node.type === 'list-item') {
        if (!node.uuid) {
          Transforms.setNodes(editor, { uuid: uuidv4() }, { at: path });
        }
        const parent = Node.parent(editor, path);
        if (!parent || !SlateElement.isElement(parent) || !LIST_TYPES.includes(parent.type)) {
          Transforms.setNodes(editor, { type: 'paragraph' }, { at: path });
          return;
        }
      }

      normalizeNode(entry);
    };

    editor.deleteBackward = (unit) => {
      const { selection } = editor;
      if (selection && Range.isCollapsed(selection)) {
        const [liEntry] = Editor.nodes(editor, {
          match: (n) => SlateElement.isElement(n) && n.type === 'list-item',
        });
        if (liEntry) {
          const [liNode, liPath] = liEntry;
          const start = Editor.start(editor, liPath);
          if (Point.equals(selection.anchor, start) && Node.string(liNode).length === 0) {
            Transforms.setNodes(editor, { type: 'paragraph' }, { at: liPath });
            Transforms.unwrapNodes(editor, {
              match: (n) => SlateElement.isElement(n) && LIST_TYPES.includes(n.type),
              split: true,
            });
            return;
          }
        }
      }
      deleteBackward(unit);
    };

    return editor;
  };

export const isListActive = (editor, format) => {
  const [match] = Editor.nodes(editor, {
    match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
  });
  return !!match;
};

export const toggleList = (editor, format) => {
  if (!editor.selection) return;

  const isListFormat = LIST_TYPES.includes(format);

  // Find list-item and parent list around the current selection (if any)
  const [listItemEntry] = Editor.nodes(editor, {
    match: (n) => SlateElement.isElement(n) && n.type === 'list-item',
  });

  const [parentListEntry] = Editor.nodes(editor, {
    match: (n) => SlateElement.isElement(n) && LIST_TYPES.includes(n.type),
  });

  const inListItem = !!listItemEntry;
  const currentListType = parentListEntry ? parentListEntry[0].type : null;

  Editor.withoutNormalizing(editor, () => {
    if (inListItem) {
      if (isListFormat) {
        if (currentListType === format) {
          const [, liPath] = listItemEntry;
          Transforms.setNodes(editor, { type: 'paragraph' }, { at: liPath });
          Transforms.unwrapNodes(editor, {
            match: (n) => SlateElement.isElement(n) && LIST_TYPES.includes(n.type),
            split: true,
          });
        } else {
          const [, listPath] = parentListEntry;
          Transforms.setNodes(editor, { type: format }, { at: listPath });
        }
      } else {
        const [, liPath] = listItemEntry;
        Transforms.setNodes(editor, { type: 'paragraph' }, { at: liPath });
        Transforms.unwrapNodes(editor, {
          match: (n) => SlateElement.isElement(n) && LIST_TYPES.includes(n.type),
          split: true,
        });
      }
    } else {
      if (isListFormat) {
        wrapParagraphsIntoList(editor, format);
      } else {
        Transforms.setNodes(
          editor,
          { type: 'paragraph' },
          {
            match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n),
            split: true,
          }
        );
      }
    }
  });
};

const wrapParagraphsIntoList = (editor, format) => {
  if (!editor.selection) return;

  if (!LIST_TYPES.includes(format)) return;

  // Get all selected blocks
  const blocks = Array.from(
    Editor.nodes(editor, {
      at: editor.selection,
      match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n) && n.type === 'paragraph',
      mode: 'lowest',
    })
  );
  if (!blocks.length) return;

  Editor.withoutNormalizing(editor, () => {
    const { selection } = editor;
    const [start, end] = Range.edges(selection);

    // Find the first and last paragraph fully or partially in selection
    const startBlock = Editor.above(editor, {
      at: start,
      match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n),
    });
    const endBlock = Editor.above(editor, {
      at: end,
      match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n),
    });

    if (!startBlock || !endBlock) return;

    const [, startPath] = startBlock;
    const [, endPath] = endBlock;

    // Collect all block nodes between the start and end paragraphs (inclusive)
    const blocks = Array.from(
      Editor.nodes(editor, {
        at: Editor.range(editor, startPath, endPath),
        match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n),
        mode: 'lowest',
      })
    );

    if (blocks.length === 0) return;

    // Unwrap existing lists within the range
    Transforms.unwrapNodes(editor, {
      match: (n) => SlateElement.isElement(n) && LIST_TYPES.includes(n.type),
      at: Editor.range(editor, startPath, endPath),
      split: true,
    });

    // Convert paragraphs to list-items
    for (const [, path] of blocks) {
      Transforms.setNodes(editor, { type: 'list-item', uuid: uuidv4() }, { at: path });
    }

    // Wrap all list-items in a list container
    const list = { type: format, uuid: uuidv4(), children: [] };
    Transforms.wrapNodes(editor, list, {
      at: Editor.range(editor, startPath, endPath),
      match: (n) => SlateElement.isElement(n) && n.type === 'list-item',
      split: true,
    });
  });
};
